import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BASE_URL from "../config";
import { CACI_HEADER_CSS } from "./CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   HEADER
   ───────────────────────────────────────────────────────────────────────────
   THE TOP BAND IS UNCHANGED. #header_center — logo, the institution's name,
   the slogan, phone and e-mail — is carried over exactly as it was, same
   markup, same Bootstrap classes, same inline styles, so it keeps rendering
   from the stylesheet it already uses. Nothing in this file or in
   CACI_HEADER_CSS touches it.

   Only the navigation strip below it was rebuilt. It now lives in
   .caci-navbar and is styled by CACI_HEADER_CSS in CaciUI.js — ink with brass
   accents, matching CACI_FOOTER_CSS, so the white band on top and the dark bar
   underneath keep the rhythm the site already had. Every class is new (hd-*),
   so no leftover rule for .header-container2 or .submenu-container interferes.

   BUGS FIXED IN THE MENU

   · "Événements" appeared twice. One came from /api/first-menu, one from the
     hard-coded link after it. Their paths differ, so the built-in links are
     now skipped when the API menu already carries that path OR that label,
     compared with accents and case folded — "Evènements" matches "Événements".
   · Two chatbots. Header and Footer each rendered <CACIChatbot/>, so the app
     mounted the widget twice. Only the footer does now.
   · Searching in Arabic or English went to a bare /search, dropping the
     language prefix and landing the visitor on the French results page.
   · Switching language threw away the query string and the hash, so changing
     language on /search?keyword=export gave you an empty search box. Same fix
     the footer already carries.
   · handleMouseLeave started a 200ms timer and never kept the id. Re-entering
     a menu could not cancel the pending close, so a quick out-and-back still
     shut the dropdown, and a stray timer could fire after unmount.
   · menuRef was declared and never used — there was no click-outside handling
     at all. The language menu, the search panel and the dropdowns stayed open
     until you clicked exactly the right button again.
   · No Escape key, no scroll lock behind the open drawer, no focus on the
     search field when it opened, no aria-current on the active link.
   · `item.status === 1` dropped every item whose status arrived as the string
     "1". Compared numerically now.
   · A failed menu request logged nothing and left the nav empty. It logs, and
     the built-in links still render.
   · The Bootstrap Icons stylesheet was injected at module scope, so importing
     this file mutated the document. It is in an effect now.
   · The injected stylesheet also carried
     `.caci-page .pg-head { margin-top: 150px }` — a global rule reaching into
     every migrated page from the header. Gone, along with the rest of the
     injected block.
   · The menu collapses into a drawer at 1100px, not 768px. With nine
     top-level items it was wrapping onto three rows well before 768.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Keep in sync with the @media (max-width: 1100px) block in CACI_HEADER_CSS. */
const MOBILE_MAX = 1100;

const LANGUAGES = [
  { code: "fr", label: "Français", short: "FR" },
  { code: "ar", label: "العربية", short: "ع" },
  { code: "en", label: "English", short: "EN" },
];

/* Rendered after the API menu, unless the API menu already covers them. */
const BUILTIN_LINKS = [
  { path: "/evenements", key: "menu.evenements", fallback: "Événements" },
  { path: "/calendrier", key: "menu.calendrier", fallback: "Calendrier" },
  { path: "/contact", key: "menu.contact", fallback: "Contact" },
];

/* Fold case and strip diacritics so "Evènements" from the CMS matches
   "Événements" from the translation file. */
const fold = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

/* Items with no status column are treated as visible; a status of 1, number or
   string, is visible. The old check was `=== 1`, which silently dropped every
   item whose status arrived as "1". */
const visible = (item) =>
  !!item && (item.status === undefined || item.status === null || Number(item.status) === 1);

const kidsOf = (item) => (Array.isArray(item?.child) ? item.child.filter(visible) : []);

/* The raw target the CMS describes, before any language prefix. */
function rawLink(item) {
  const link = String(item?.link ?? "").trim();
  if (item?.type && item?.id_item) {
    return `${item.type}/${item.id_item}/${link}`.replace(/\/+$/, "");
  }
  return link;
}

/* A parent whose own link is "#" or empty is only a toggle. One with a real
   target gets a link plus a separate caret, so its page stays reachable. */
const isRealLink = (item) => {
  const l = rawLink(item);
  return !!l && l !== "#" && !/^javascript:/i.test(l);
};

/* Was `/${link}`, which produced "//page/3" whenever the CMS stored a leading
   slash — a path React Router will not match. */
function pathOf(item) {
  const raw = rawLink(item).replace(/^\/+/, "");
  if (!raw) return "/";
  return `/${raw}`.replace(/\/+$/, "") || "/";
}

function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [menuItems, setMenuItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window === "undefined" || window.innerWidth > MOBILE_MAX
  );

  const navbarRef = useRef(null);
  const searchInputRef = useRef(null);
  const hoverTimers = useRef({});

  /* ── Bootstrap Icons ──────────────────────────────────────────────────────
     In an effect, not at module scope: importing this file should not mutate
     the document. */
  useEffect(() => {
    if (document.querySelector('link[href*="bootstrap-icons"]')) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css";
    document.head.appendChild(link);
  }, []);

  /* ── Menu ─────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(`${BASE_URL}/api/first-menu`, { signal: ctrl.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setMenuItems(Array.isArray(data) ? data.filter(visible) : []))
      .catch((err) => {
        if (err.name === "AbortError") return;
        /* Previously silent: a broken endpoint left an empty nav with nothing
           in the console to explain it. The built-in links still render. */
        console.error("Header: /api/first-menu failed to load —", err);
      });
    return () => ctrl.abort();
  }, []);

  /* ── Viewport ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const read = () => setIsDesktop(window.innerWidth > MOBILE_MAX);
    read();
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, []);

  /* Crossing into desktop with the drawer open left it stuck open behind the
     inline menu. */
  useEffect(() => {
    if (isDesktop) setNavOpen(false);
    setOpenMenus({});
  }, [isDesktop]);

  const closeAll = useCallback(() => {
    setNavOpen(false);
    setLangOpen(false);
    setSearchOpen(false);
    setOpenMenus({});
  }, []);

  /* Navigating closes everything. */
  useEffect(() => { closeAll(); }, [location.pathname, closeAll]);

  /* ── Escape ───────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeAll(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeAll]);

  /* ── Click outside ────────────────────────────────────────────────────────
     menuRef existed for this and was never used, so nothing ever closed by
     clicking away. */
  useEffect(() => {
    const onDown = (e) => {
      if (!navbarRef.current) return;
      if (navbarRef.current.contains(e.target)) return;
      closeAll();
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [closeAll]);

  /* ── Scroll lock behind the drawer ────────────────────────────────────────── */
  useEffect(() => {
    if (!navOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [navOpen]);

  /* ── Search field focus ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (searchOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [searchOpen]);

  /* ── Hover intent ─────────────────────────────────────────────────────────
     The old version called setTimeout on mouse leave and never kept the id, so
     re-entering could not cancel the pending close and a stray timer could
     fire after unmount. */
  const clearHover = (id) => {
    const timer = hoverTimers.current[id];
    if (timer) {
      clearTimeout(timer);
      delete hoverTimers.current[id];
    }
  };

  const hoverOpen = (id) => {
    if (!isDesktop) return;
    clearHover(id);
    setOpenMenus((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };

  const hoverClose = (id) => {
    if (!isDesktop) return;
    clearHover(id);
    hoverTimers.current[id] = setTimeout(() => {
      delete hoverTimers.current[id];
      setOpenMenus((prev) => ({ ...prev, [id]: false }));
    }, 180);
  };

  useEffect(() => {
    const timers = hoverTimers.current;
    return () => Object.keys(timers).forEach((k) => clearTimeout(timers[k]));
  }, []);

  const toggleMenu = (id) => {
    clearHover(id);
    setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  /* ── Paths ────────────────────────────────────────────────────────────────── */
  const withLang = useCallback(
    (p) => (lang === "fr" ? p : `/${lang}${p === "/" ? "" : p}`),
    [lang]
  );

  const barePath = location.pathname.replace(/^\/(ar|en|fr)(?=\/|$)/, "") || "/";
  const isCurrent = (p) =>
    p === "/" ? barePath === "/" : barePath === p || barePath.startsWith(`${p}/`);

  const label = (item) => {
    if (lang === "ar") return item.label_ar || item.label;
    if (lang === "en") return item.label_en || item.label;
    return item.label;
  };

  /* ── Language ─────────────────────────────────────────────────────────────── */
  const changeLanguage = (next) => {
    setLangOpen(false);
    if (next === lang) return;
    i18n.changeLanguage(next);

    /* The query string and the hash are part of where the visitor is. Dropping
       them meant switching language on /search?keyword=export threw the search
       away. */
    const tail = `${location.search || ""}${location.hash || ""}`;
    const target = next === "fr" ? barePath : `/${next}${barePath === "/" ? "" : barePath}`;
    navigate(`${target}${tail}`);
  };

  /* ── Search ───────────────────────────────────────────────────────────────── */
  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchTerm.trim();
    if (!q) {
      if (searchInputRef.current) searchInputRef.current.focus();
      return;
    }
    /* Was a bare "/search", which sent an Arabic or English visitor to the
       French results page. */
    navigate(`${withLang("/search")}?keyword=${encodeURIComponent(q)}`);
    closeAll();
  };

  /* ── Built-in links, minus anything the CMS already provides ─────────────── */
  const builtins = useMemo(() => {
    const seen = new Set();
    const walk = (items) =>
      items.forEach((item) => {
        seen.add(fold(pathOf(item)));
        seen.add(fold(item.label));
        seen.add(fold(item.label_ar));
        seen.add(fold(item.label_en));
        walk(kidsOf(item));
      });
    walk(menuItems);
    seen.delete("");

    /* The screenshot showed "Evènements" from the API and "Événements" from
       here side by side in the same bar. Their paths differ, so labels are
       compared as well, accents and case folded. */
    return BUILTIN_LINKS.filter(
      (b) => !seen.has(fold(b.path)) && !seen.has(fold(t(b.key, b.fallback)))
    );
  }, [menuItems, t]);

  /* ── Contact details for the drawer ───────────────────────────────────────
     The band above hides its contact column on small screens (hidden-xs), so
     the same phone and e-mail are repeated at the foot of the open drawer.
     t("menu.phone") is a display string ("028 12 95 96 / Fax : 020 05 29 60"),
     so a tel: href is built from the digits before the slash, and only when
     that yields a plausible number. */
  const phoneText = String(t("menu.phone", "")).trim();
  const phoneDigits = (phoneText.split("/")[0] || "").replace(/[^\d+]/g, "");
  const phoneHref = /^\+?\d{9,13}$/.test(phoneDigits) ? `tel:${phoneDigits}` : null;

  const emailText = String(t("menu.email", "")).trim();
  const emailHref = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailText) ? `mailto:${emailText}` : null;

  const active = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  /* ── Menu nodes ───────────────────────────────────────────────────────────
     A plain render function, not a component declared in the body: the element
     type stays <li>, so React reconciles instead of remounting. */
  const renderNodes = (items, level) =>
    items.map((item, idx) => {
      const kids = kidsOf(item);
      const hasKids = kids.length > 0;
      const key = item.id ?? `${level}-${idx}`;
      const open = !!openMenus[key];
      const p = pathOf(item);
      const current = isCurrent(p);
      const text = label(item);

      return (
        <li
          key={key}
          className={`hd-item level-${level}${hasKids ? " has-sub" : ""}${open ? " is-open" : ""}`}
          onMouseEnter={hasKids ? () => hoverOpen(key) : undefined}
          onMouseLeave={hasKids ? () => hoverClose(key) : undefined}
        >
          {/* A leaf whose CMS link is "#" or empty used to render
              <Link to="/#"> — a dead route. It is plain text now. */}
          {!hasKids && !isRealLink(item) && (
            <span className="hd-link is-static" dir="auto">{text}</span>
          )}

          {!hasKids && isRealLink(item) && (
            <Link
              to={withLang(p)}
              className={`hd-link${current ? " is-current" : ""}`}
              aria-current={current ? "page" : undefined}
              onClick={closeAll}
            >
              {text}
            </Link>
          )}

          {hasKids && isRealLink(item) && (
            <span className="hd-node">
              <Link
                to={withLang(p)}
                className={`hd-link${current ? " is-current" : ""}`}
                aria-current={current ? "page" : undefined}
                onClick={closeAll}
              >
                {text}
              </Link>
              <button
                type="button"
                className="hd-caret"
                aria-expanded={open}
                aria-label={`${text} — ${t("menu.submenu", "sous-menu")}`}
                onClick={() => toggleMenu(key)}
              >
                <i className="bi bi-chevron-down" aria-hidden="true" />
              </button>
            </span>
          )}

          {hasKids && !isRealLink(item) && (
            <button
              type="button"
              className="hd-trigger"
              aria-expanded={open}
              onClick={() => toggleMenu(key)}
            >
              <span>{text}</span>
              <i className="bi bi-chevron-down" aria-hidden="true" />
            </button>
          )}

          {hasKids && (
            <div className="hd-sub">
              <ul>{renderNodes(kids, level + 1)}</ul>
            </div>
          )}
        </li>
      );
    });

  return (
    <header className="site-header">
      {/* ══════════════════════════════════════════════════════════════════
          INSTITUTIONAL BAND — UNCHANGED
          Same markup, same classes, same inline styles as before. Left
          exactly as it was on purpose; do not restyle from CaciUI.
          ══════════════════════════════════════════════════════════════════ */}
      <div id="header_center">
        <div className="container mx-auto text-center">
          <div className="row flex justify-center">
            <div className="col-sm-2 col-lg-2 col-md-2 col-xs-12 logo">
              <div className="row">
                <Link to={i18n.language === 'fr' ? '/' : `/${i18n.language}`}>
                  <img src="/logo.png" alt="CACI Logo" className="img-responsive" />
                </Link>
              </div>
            </div>

            <div className="col-sm-12 col-lg-8 col-md-8 col-xs-12 slogo wow flipInX animated"
                style={{visibility: 'visible', animationName: 'flipInX'}}>
              <h4 className="arab">
              {t('menu.slog_01')}
              </h4>
             <br />

              <h4>
              {t('menu.slog_02')}
              </h4>
            </div>

            <div className="col-sm-2 col-lg-2 col-md-2 hidden-xs">
              <div className="flex flex-col space-y-3 p-4">
                <div className="flex items-center justify-center">
                  <i className="bi bi-telephone text-primary text-primary2 fs-4 text-blue-500 w-5 h-5 mr-2"></i>
                  <span className="font-medium text-gray-700" style={{ marginLeft: '5px' }}>{t('menu.phone')}</span>
                </div>

                <div className="flex items-center justify-center">
                  <i className="bi bi-envelope text-primary text-primary2 fs-4 text-blue-500 w-5 h-5 mr-2"></i>
                  <span className="font-medium text-gray-700" style={{ marginLeft: '5px' }}>{t('menu.email')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MENU BAR — the only part rebuilt
          ══════════════════════════════════════════════════════════════════ */}
      <div
        ref={navbarRef}
        className={`caci-navbar${isRtl ? " is-rtl" : ""}${navOpen ? " is-open" : ""}`}
        dir={isRtl ? "rtl" : "ltr"}
      >
        <style>{CACI_HEADER_CSS}</style>

        <div className="hd-thread" aria-hidden="true" />

        <div className="hd-shell hd-bar-in">
          <nav className="hd-nav" aria-label={t("menu.main_nav", "Navigation principale")}>
            <ul className="hd-menu">
              {renderNodes(menuItems, 0)}

              {builtins.map((b) => {
                const current = isCurrent(b.path);
                return (
                  <li key={b.path} className="hd-item level-0">
                    <Link
                      to={withLang(b.path)}
                      className={`hd-link${current ? " is-current" : ""}`}
                      aria-current={current ? "page" : undefined}
                      onClick={closeAll}
                    >
                      {t(b.key, b.fallback)}
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Shown by CSS only below 1100px, where the band above hides its
                contact column. */}
            <div className="hd-drawer-meta">
              {phoneText && (
                phoneHref ? (
                  <a href={phoneHref}>
                    <i className="bi bi-telephone" aria-hidden="true" />
                    <span dir="ltr">{phoneText}</span>
                  </a>
                ) : (
                  <span>
                    <i className="bi bi-telephone" aria-hidden="true" />
                    <span dir="ltr">{phoneText}</span>
                  </span>
                )
              )}
              {emailText && (
                emailHref ? (
                  <a href={emailHref}>
                    <i className="bi bi-envelope" aria-hidden="true" />
                    <span dir="ltr">{emailText}</span>
                  </a>
                ) : (
                  <span>
                    <i className="bi bi-envelope" aria-hidden="true" />
                    <span dir="ltr">{emailText}</span>
                  </span>
                )
              )}
            </div>
          </nav>

          <div className="hd-tools">
            <div className="hd-lang">
              <button
                type="button"
                className="hd-lang-btn"
                aria-expanded={langOpen}
                aria-haspopup="true"
                onClick={() => { setLangOpen((v) => !v); setSearchOpen(false); }}
              >
                <i className="bi bi-globe2" aria-hidden="true" />
                <span>{active.short}</span>
              </button>
              {langOpen && (
                <div className="hd-lang-menu" role="menu">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      role="menuitem"
                      className={`hd-lang-opt${l.code === lang ? " is-active" : ""}`}
                      onClick={() => changeLanguage(l.code)}
                    >
                      <span>{l.label}</span>
                      {l.code === lang && <i className="bi bi-check2" aria-hidden="true" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              className={`hd-icon-btn${searchOpen ? " is-on" : ""}`}
              aria-expanded={searchOpen}
              aria-label={t("search.placeholder", "Rechercher")}
              onClick={() => { setSearchOpen((v) => !v); setLangOpen(false); }}
            >
              <i className={`bi bi-${searchOpen ? "x-lg" : "search"}`} aria-hidden="true" />
            </button>

            <button
              type="button"
              className="hd-icon-btn hd-burger"
              aria-expanded={navOpen}
              aria-label={t("menu.menu", "Menu")}
              onClick={() => { setNavOpen((v) => !v); setLangOpen(false); setSearchOpen(false); }}
            >
              <i className={`bi bi-${navOpen ? "x-lg" : "list"}`} aria-hidden="true" />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="hd-search">
            <div className="hd-shell">
              <form onSubmit={submitSearch} role="search">
                <input
                  ref={searchInputRef}
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t("search.placeholder", "Rechercher…")}
                  aria-label={t("search.placeholder", "Rechercher")}
                />
                <button type="submit" className="hd-search-go">
                  <i className="bi bi-search" aria-hidden="true" />
                  {t("search.submit", "Rechercher")}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
